/**
 * ConfirmationModal - Modal de confirmación reutilizable
 *
 * QUÉ HACE:
 * - Muestra un diálogo de confirmación con variantes visuales (danger, warning, success, info)
 * - Usa el componente Modal genérico como base
 * - Usa el componente Button del design system
 *
 * QUÉ EXPORTA: ConfirmationModal (named)
 *
 * QUÉ NO DEBE HACER:
 * - NO debe implementar su propio overlay/backdrop (usa Modal)
 * - NO debe tener botones con estilos inline (usa Button)
 */

import React from "react";
import { AlertTriangle, Info, CheckCircle } from "lucide-react";
import { Modal } from "@/components/ui";
import { Button } from "@/components/ui";

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  variant?: "danger" | "warning" | "info" | "success";
}

const variantConfig = {
  danger: {
    icon: <AlertTriangle className="text-red-500" size={24} />,
    bgIcon: "bg-red-50 dark:bg-red-900/30",
    buttonVariant: "danger" as const,
  },
  warning: {
    icon: <AlertTriangle className="text-amber-500" size={24} />,
    bgIcon: "bg-amber-50 dark:bg-amber-900/30",
    buttonVariant: "primary" as const,
  },
  success: {
    icon: <CheckCircle className="text-green-500" size={24} />,
    bgIcon: "bg-green-50 dark:bg-green-900/30",
    buttonVariant: "success" as const,
  },
  info: {
    icon: <Info className="text-blue-500" size={24} />,
    bgIcon: "bg-blue-50 dark:bg-blue-900/30",
    buttonVariant: "primary" as const,
  },
};

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Confirmar",
  cancelText = "Cancelar",
  variant = "danger",
}) => {
  const config = variantConfig[variant];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      showCloseButton={false}
      compactOnMobile
      bodyClassName="!p-0 !overflow-visible !max-h-none"
      footer={
        <div className="flex w-full gap-2 sm:gap-3">
          <Button
            variant="secondary"
            size="sm"
            onClick={onClose}
            className="flex-1"
          >
            {cancelText}
          </Button>
          <Button
            variant={config.buttonVariant}
            size="sm"
            className="flex-1"
            onClick={() => {
              onConfirm();
              onClose();
            }}
          >
            {confirmText}
          </Button>
        </div>
      }
    >
      <div className="border-b border-gray-100 dark:border-slate-700 px-3 sm:px-4 py-2.5 sm:py-3">
        <div className="flex items-start gap-2.5 sm:gap-3 min-w-0">
          <div
            className={`p-2 sm:p-2.5 rounded-full flex-shrink-0 [&_svg]:w-5 [&_svg]:h-5 sm:[&_svg]:w-6 sm:[&_svg]:h-6 ${config.bgIcon}`}
          >
            {config.icon}
          </div>
          <h3 className="text-sm sm:text-base font-bold text-gray-900 dark:text-gray-100 leading-snug break-words pt-0.5">
            {title}
          </h3>
        </div>
      </div>
      <div className="px-3 sm:px-4 pt-2 sm:pt-2.5 pb-3 sm:pb-4 text-xs sm:text-sm text-gray-500 dark:text-gray-400 leading-snug">
        {message}
      </div>
    </Modal>
  );
};

export default ConfirmationModal;
